// src/merchant/MerchantEarnings.js
import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useApp } from '../context/AppContext';
import { IndianRupee, TrendingUp, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <span className="text-gray-400 font-body text-sm">{label}</span>
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}>
          <Icon size={18} className="text-white" />
        </div>
      </div>
      <p className="font-display font-bold text-2xl text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 font-body mt-1">{sub}</p>}
    </div>
  );
}

export default function MerchantEarnings() {
  const { user }          = useApp();
  const [orders,  setOrders]  = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const fetchData = async () => {
      try {
        // ── Only this merchant's sub-orders ───────────────────────
        const q    = query(
          collection(db, 'merchantOrders'),
          where('merchantId', '==', user.uid)
        );
        const snap = await getDocs(q);
        const myOrders = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        myOrders.sort((a, b) => (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0));
        setOrders(myOrders);

        // Get merchant profile for commission rate
        const profileSnap = await getDoc(doc(db, 'merchants', user.uid));
        if (profileSnap.exists()) setProfile(profileSnap.data());
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const commissionRate = profile?.commissionRate || 10;

  const totalRevenue    = orders.filter((o) => o.status !== 'cancelled').reduce((s, o) => s + (o.total || 0), 0);
  const commission      = Math.round(totalRevenue * commissionRate / 100);
  const netEarnings     = totalRevenue - commission;
  const pendingOrders   = orders.filter((o) => !['delivered', 'cancelled'].includes(o.status));
  const pendingAmount   = Math.round(pendingOrders.reduce((s, o) => s + (o.total || 0), 0) * (1 - commissionRate / 100));
  const paidOut         = Math.round(orders.filter((o) => o.status === 'delivered').reduce((s, o) => s + (o.total || 0), 0) * (1 - commissionRate / 100));

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-10 h-10 border-4 border-rose-100 border-t-rose-500 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display font-bold text-xl text-gray-900">My Earnings</h2>
        <p className="text-gray-400 font-body text-sm">
          Platform commission: <span className="text-rose-500 font-semibold">{commissionRate}%</span>
          {' '}· Your share: <span className="text-green-600 font-semibold">{100 - commissionRate}%</span>
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={IndianRupee} label="Total Revenue"   value={`₹${totalRevenue.toLocaleString('en-IN')}`}  sub="Excl. cancelled" color="bg-blue-500"   />
        <StatCard icon={TrendingUp}  label="Net Earnings"    value={`₹${netEarnings.toLocaleString('en-IN')}`}   sub="After commission" color="bg-green-500"  />
        <StatCard icon={Clock}       label="Pending Payout"  value={`₹${pendingAmount.toLocaleString('en-IN')}`} sub="In transit"       color="bg-amber-500"  />
        <StatCard icon={CheckCircle} label="Total Paid Out"  value={`₹${paidOut.toLocaleString('en-IN')}`}       sub="Delivered orders"  color="bg-rose-500"   />
      </div>

      {/* How it works */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <h3 className="font-display font-semibold text-gray-900 mb-4">Earnings Breakdown</h3>
        <div className="space-y-3">
          {[
            { label: 'Customer pays',                                  value: `₹${totalRevenue.toLocaleString('en-IN')}`,  color: 'text-gray-900' },
            { label: `Platform commission (${commissionRate}%)`,       value: `- ₹${commission.toLocaleString('en-IN')}`,  color: 'text-red-500'  },
            { label: 'Your net earnings',                              value: `₹${netEarnings.toLocaleString('en-IN')}`,   color: 'text-green-600' },
          ].map(({ label, value, color }) => (
            <div key={label} className="flex justify-between items-center py-2 border-b border-gray-50">
              <span className="font-body text-gray-600 text-sm">{label}</span>
              <span className={`font-display font-bold text-lg ${color}`}>{value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Recent transactions */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="font-display font-semibold text-gray-900">Recent Orders</h3>
        </div>
        {orders.length === 0 ? (
          <div className="text-center py-12 text-gray-400 font-body">No orders yet</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {orders.slice(0, 10).map((order) => {
              const net  = Math.round((order.total || 0) * (1 - commissionRate / 100));
              const date = order.createdAt?.toDate?.()?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) || '—';
              return (
                <div key={order.id} className="flex items-center gap-4 px-6 py-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-body font-semibold text-gray-900 text-sm">#{order.id.slice(-6).toUpperCase()}</p>
                    <p className="font-body text-gray-400 text-xs">{date} · {order.shippingAddress?.name}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-display font-bold text-green-600 text-sm">+₹{net.toLocaleString('en-IN')}</p>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                      order.status === 'shipped'   ? 'bg-purple-100 text-purple-700' :
                      order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {order.status || 'confirmed'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Bank not set warning */}
      {!profile?.accountNumber && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <AlertCircle size={20} className="text-amber-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-body font-semibold text-amber-700 text-sm">Bank details missing</p>
            <p className="font-body text-amber-600 text-xs mt-0.5">Add your bank account to receive payouts.</p>
          </div>
          <Link to="/merchant/profile"
            className="shrink-0 bg-amber-500 text-white text-xs font-body font-semibold px-3 py-1.5 rounded-xl hover:bg-amber-600 transition-colors">
            Add Bank →
          </Link>
        </div>
      )}
    </div>
  );
}
